<template>
  <v-dialog v-model="show" max-width="450" persistent class="interaction-settings-dialog">
    <v-card class="rounded-xl bg-surface-dark border border-white-05 overflow-hidden">
      <v-card-title class="d-flex align-center justify-space-between pa-4 border-b border-white-05">
        <span class="text-h6 font-weight-bold">Post interaction settings</span>
        <v-btn icon="mdi-close" variant="text" size="small" @click="$emit('close')" color="primary"></v-btn>
      </v-card-title>

      <v-card-text class="pa-4 scroll-container">
        <div class="text-subtitle-1 font-weight-bold mb-4">Who can reply</div>

        <!-- Anyone / Nobody Radio -->
        <div class="radio-toggle-container mb-4">
          <div 
            class="radio-box" 
            :class="{ active: localSettings.replyOption === 'anyone' }"
            @click="setReplyOption('anyone')"
          >
            <v-icon :icon="localSettings.replyOption === 'anyone' ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'" color="primary"></v-icon>
            <span class="ml-2 font-weight-medium">Anyone</span>
          </div>
          <div 
            class="radio-box" 
            :class="{ active: localSettings.replyOption === 'nobody' }"
            @click="setReplyOption('nobody')"
          >
            <v-icon :icon="localSettings.replyOption === 'nobody' ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'" color="primary"></v-icon>
            <span class="ml-2 font-weight-medium">Nobody</span>
          </div>
        </div>

        <!-- Custom Checklist -->
        <div class="checklist-container mb-4">
          <div 
            v-for="opt in ruleOptions" 
            :key="opt.value"
            class="check-box mb-2"
            :class="{ 
              active: localSettings?.replyRules?.[opt.value],
              disabled: localSettings.replyOption !== 'custom' && localSettings.replyOption !== 'anyone' && localSettings.replyOption !== 'custom_init'
            }"
            @click="toggleRule(opt.value)"
          >
            <v-icon :icon="localSettings?.replyRules?.[opt.value] ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'"></v-icon>
            <span class="ml-3">{{ opt.label }}</span>
          </div>
        </div>

        <!-- Lists Collapsible -->
        <v-expansion-panels variant="accordion" class="mb-4 list-expansion">
          <v-expansion-panel class="bg-transparent border-none">
            <template v-slot:title>
              <div class="d-flex align-center w-100">
                <span class="text-body-1">Select from your lists</span>
              </div>
            </template>
            <v-expansion-panel-text>
              <div v-if="loadingLists" class="d-flex justify-center py-4">
                <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
              </div>
              <div v-else-if="lists.length === 0" class="text-caption text-grey py-2 px-4">
                No lists found.
              </div>
              <div v-else class="list-container">
                <div 
                  v-for="list in lists" 
                  :key="list.uri"
                  class="check-box list-item mb-1"
                  :class="{ active: isListSelected(list.uri) }"
                  @click="toggleList(list.uri)"
                >
                  <v-icon :icon="isListSelected(list.uri) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'"></v-icon>
                  <v-avatar size="24" class="ml-3 mr-2" rounded="sm">
                    <v-img v-if="list.avatar" :src="list.avatar"></v-img>
                    <v-icon v-else icon="mdi-account-group" size="16"></v-icon>
                  </v-avatar>
                  <span class="text-body-2 text-truncate">{{ list.name }}</span>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Quote Toggle -->
        <div class="quote-toggle pa-4 rounded-xl d-flex align-center justify-space-between bg-surface-variant-subtle mb-4">
          <div class="d-flex align-center">
            <v-icon icon="mdi-format-quote-open" class="mr-3"></v-icon>
            <span class="font-weight-bold">Allow quote posts</span>
          </div>
          <v-switch
            v-model="localSettings.allowQuotes"
            color="primary"
            hide-details
            density="compact"
          ></v-switch>
        </div>

        <div class="text-caption text-grey px-2 mb-2">
          These are your default settings
        </div>
      </v-card-text>

      <v-card-actions class="pa-4 bg-surface-dark border-t border-white-05">
        <v-btn
          color="primary"
          block
          rounded="pill"
          class="save-btn text-none font-weight-black"
          size="large"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { getMyLists } from '@/services/bluesky';

const props = defineProps<{
  modelValue: boolean;
  settings: any;
}>();

const emit = defineEmits(['update:modelValue', 'close', 'save']);

const show = ref(props.modelValue);
const loadingLists = ref(false);
const lists = ref<any[]>([]);

const ruleOptions = [
  { label: 'Your followers', value: 'followers' },
  { label: 'People you follow', value: 'following' },
  { label: 'People you mention', value: 'mentioned' }
] as const;

const localSettings = ref({
  replyOption: 'anyone',
  replyRules: {
    followers: false,
    following: false,
    mentioned: false,
    lists: [] as string[]
  },
  allowQuotes: true
});

watch(() => props.modelValue, (val) => {
  show.value = val;
  if (val) {
    loadSettings();
    fetchLists();
  }
});

watch(show, (val) => {
  emit('update:modelValue', val);
});

const loadSettings = () => {
  if (props.settings) {
    const settings = JSON.parse(JSON.stringify(props.settings));
    // Ensure replyRules is never undefined
    if (!settings.replyRules) {
      settings.replyRules = {
        followers: false,
        following: false,
        mentioned: false,
        lists: []
      };
    }
    localSettings.value = settings;
  }
};

const fetchLists = async () => {
  if (lists.value.length > 0) return;
  loadingLists.value = true;
  try {
    lists.value = await getMyLists();
  } catch (e) {
    console.error(e);
  } finally {
    loadingLists.value = false;
  }
};

const setReplyOption = (option: 'anyone' | 'nobody') => {
  localSettings.value.replyOption = option;
  if (option === 'anyone' || option === 'nobody') {
    // Reset checklists
    localSettings.value.replyRules = {
      followers: false,
      following: false,
      mentioned: false,
      lists: []
    };
  }
};

const toggleRule = (rule: 'followers' | 'following' | 'mentioned') => {
  // If we toggle a checklist item, switch from anyone/nobody to custom
  if (localSettings.value.replyOption !== 'custom') {
    localSettings.value.replyOption = 'custom';
  }
  localSettings.value.replyRules[rule] = !localSettings.value.replyRules[rule];
  
  // If no rules are selected, revert to anyone? Actually Bluesky keeps it as empty "Nobody" if all unchecked
  checkEmptyRules();
};

const toggleList = (listUri: string) => {
  if (localSettings.value.replyOption !== 'custom') {
    localSettings.value.replyOption = 'custom';
  }
  const index = localSettings.value.replyRules.lists.indexOf(listUri);
  if (index === -1) {
    localSettings.value.replyRules.lists.push(listUri);
  } else {
    localSettings.value.replyRules.lists.splice(index, 1);
  }
  checkEmptyRules();
};

const isListSelected = (listUri: string) => {
  return localSettings.value?.replyRules?.lists?.includes(listUri) || false;
};

const checkEmptyRules = () => {
  const r = localSettings.value.replyRules;
  const hasRules = r.followers || r.following || r.mentioned || r.lists.length > 0;
  if (!hasRules && localSettings.value.replyOption === 'custom') {
    // If custom but no rules, it's effectively "Nobody"
    // localSettings.value.replyOption = 'nobody';
  }
};

const save = () => {
  emit('save', JSON.parse(JSON.stringify(localSettings.value)));
  emit('close');
};

onMounted(() => {
  loadSettings();
});
</script>

<style scoped>
.interaction-settings-dialog :deep(.v-overlay__content) {
  margin: 12px;
}

.bg-surface-dark {
  background-color: rgba(11, 18, 25, 0.76) !important;
  backdrop-filter: blur(40px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(40px) saturate(180%) !important;
  color: white !important;
}

.bg-surface-variant-subtle {
  background-color: rgba(30, 48, 64, 0.3) !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.radio-toggle-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.radio-box, .check-box {
  background-color: rgba(30, 48, 64, 0.3);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.radio-box:hover, .check-box:hover {
  background-color: rgba(30, 48, 64, 0.5);
}

.radio-box.active {
  background-color: rgba(var(--v-theme-primary), 0.15);
  border-color: rgba(var(--v-theme-primary), 0.4);
}

.check-box.active {
  color: #fff;
}

.check-box.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.list-expansion :deep(.v-expansion-panel-title) {
  padding: 16px;
  min-height: 56px;
  background-color: rgba(30, 48, 64, 0.3) !important;
  border-radius: 12px !important;
}

.list-expansion :deep(.v-expansion-panel-text__wrapper) {
  padding: 12px 8px;
}

.list-expansion :deep(.v-expansion-panel--active > .v-expansion-panel-title) {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.list-item {
  padding: 12px;
  border-radius: 8px;
}

.save-btn {
  letter-spacing: 0.5px;
}

.scroll-container {
  max-height: 70vh;
  overflow-y: auto;
}

/* Custom Icons colors */
.v-icon[icon="mdi-radiobox-marked"],
.v-icon[icon="mdi-checkbox-marked"] {
  color: var(--v-theme-primary) !important;
}

:deep(.v-expansion-panel-title__overlay) {
  display: none;
}

:deep(.v-expansion-panel__shadow) {
  box-shadow: none !important;
}
</style>
